import { HttpClient } from "@angular/common/http";
import {
  ARestClient,
  ICreateRecordResponse,
  IImageUploadResponse,
  IOkResponse,
  IPage,
  ISort
} from "@zaeper/communication-lib";
import { IApplicationRestClient } from "./i-application-rest.client";
import { ICreateApplicationForm } from "./forms/i-create-application.form";
import { IUpdateApplicationForm } from "./forms/i-update-application.form";
import { IGetApplicationPageForm } from "./forms/i-get-application-page.form";
import { IGetApplicationForm } from "./forms/i-get-application.form";
import { IUploadIconImageForm } from "./forms/i-upload-icon-image.form";
import { IUploadTitleImageForm } from "./forms/i-upload-title-image.form";
import { IApplication } from "../../models/application/i-application";
import { HostUtil } from "../../utils/host-util";
import { firstValueFrom, Observable } from "rxjs";

export abstract class AApplicationRestClient extends ARestClient implements IApplicationRestClient {
  protected constructor(
    private readonly _httpClient: HttpClient,
    private readonly _entityPath: string
  ) {
    super();
  }

  createApplication(form: ICreateApplicationForm): Promise<ICreateRecordResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/${ this._entityPath }` );

    return firstValueFrom( this._httpClient.post( endpointUrl, form, { withCredentials: true } ) as Observable<ICreateRecordResponse> );
  }

  updateApplication(
    recordId: string,
    form: IUpdateApplicationForm
  ): Promise<IOkResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/${ this._entityPath }/${ recordId }` );

    return firstValueFrom( this._httpClient.put( endpointUrl, form, { withCredentials: true } ) as Observable<IOkResponse> );
  }

  deleteApplication(recordId: string): Promise<IOkResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/${ this._entityPath }/${ recordId }` );

    return firstValueFrom( this._httpClient.delete( endpointUrl, { withCredentials: true } ) as Observable<IOkResponse> );
  }

  getApplication(form: IGetApplicationForm): Promise<IApplication> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/${ this._entityPath }/${ form.recordId }` );

    return firstValueFrom( this._httpClient.get( endpointUrl, {
      withCredentials: true,
    } ) as Observable<IApplication> );
  }

  getApplicationPage(form: IGetApplicationPageForm): Promise<IPage<IApplication>> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/${ this._entityPath }/page` );

    const defaultSort: ISort = {
      field: "name",
      ascending: true
    };

    return firstValueFrom( this._httpClient.get( endpointUrl, {
      withCredentials: true,
      params: {
        pageNumber: form.pageNumber,
        pageSize: form.pageSize,
        sort: super.serializeSort( form.sort ?? defaultSort )
      }
    } ) as Observable<IPage<IApplication>> );
  }

  uploadIconImage(
    recordId: string,
    form: IUploadIconImageForm
  ): Promise<IImageUploadResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/${ this._entityPath }/${ recordId }/icon-image` );

    const formData: FormData = new FormData();
    formData.set( "iconImage", form.iconImage );
    formData.set( "iconType", form.iconType );

    return firstValueFrom( this._httpClient.post( endpointUrl, formData, {
      withCredentials: true,
    } ) as Observable<IImageUploadResponse> );
  }

  publishApplication(recordId: string): Promise<IOkResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/${ this._entityPath }/${ recordId }/publish` );

    return firstValueFrom( this._httpClient.post( endpointUrl, {}, { withCredentials: true } ) as Observable<IOkResponse> );
  }

  uploadTitleImage(
    recordId: string,
    form: IUploadTitleImageForm
  ): Promise<IImageUploadResponse> {
    const endpointUrl: string = super.getEndpointUrl( HostUtil.getApplicationApiEndpointUrl(), `v1/${ this._entityPath }/${ recordId }/title-image` );

    const formData: FormData = new FormData();
    formData.set( "titleImage", form.titleImage );

    return firstValueFrom( this._httpClient.post( endpointUrl, formData, {
      withCredentials: true,
    } ) as Observable<IImageUploadResponse> );
  }
}
